/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { FileSignature, Workflow, Trophy, ArrowRight } from 'lucide-react';

const DISPLAY = { fontFamily: "'Space Grotesk', sans-serif" };
const MONO = { fontFamily: "'JetBrains Mono', monospace" };

const STEPS = [
    {
        n: '01',
        icon: FileSignature,
        title: 'Un mandat est signé',
        desc: "Votre agence importe ou crée le mandat. Le vendeur est rattaché à un agent. Homly entre en scène.",
        accent: '#A855F7',
    },
    {
        n: '02',
        icon: Workflow,
        title: 'Homly orchestre l\'accompagnement',
        desc: "Suivi vendeur, alertes, qualité des étapes, transparence pour le manager. Les bons signaux remontent au bon moment.",
        accent: '#FF914D',
    },
    {
        n: '03',
        icon: Trophy,
        title: 'La qualité devient réputation',
        desc: "À la fin, le vendeur évalue l'agent et l'agence. Ces avis vérifiés nourrissent vos pages publiques — et attirent les prochains.",
        accent: '#22c55e',
    },
];

/**
 * ConvNarrative — Bandeau « 1 · 2 · 3 » placé juste sous le hero.
 *
 * Objectif : raconter en 5 secondes ce que fait Homly. Le fil conducteur
 * commercial devient lisible avant même de scroller — pas une grille de
 * 8 bénéfices, mais une histoire en trois actes.
 *
 * Animations B2B sérieuses :
 * - cartes qui se révèlent en stagger au scroll
 * - ligne de progression qui se dessine entre les étapes
 * - icônes qui pulsent doucement quand la carte entre dans la viewport
 */
export const ConvNarrative = () => {
    const sectionRef = useRef(null);
    const inView = useInView(sectionRef, { once: true, margin: '-80px' });

    return (
        <section
            ref={sectionRef}
            className="relative py-20 md:py-28 border-t border-white/[0.04]"
            data-testid="narrative-section"
        >
            {/* Ambient glow */}
            <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#A855F7]/30 to-transparent" />

            <div className="max-w-6xl mx-auto px-6 md:px-12">
                <div className="text-center max-w-2xl mx-auto mb-14">
                    <motion.span
                        initial={{ opacity: 0, y: 8 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.5 }}
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] text-[10px] font-medium tracking-widest text-[#A19DAB] uppercase"
                        style={MONO}
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#FF914D] animate-pulse" />
                        Comment ça marche
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 16 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.55, delay: 0.05 }}
                        className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white leading-[1.1]"
                        style={DISPLAY}
                    >
                        Du mandat signé à la{' '}
                        <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                            réputation publique.
                        </span>
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={inView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.55, delay: 0.12 }}
                        className="mt-4 text-base text-[#A19DAB] leading-relaxed"
                    >
                        Trois étapes. Une histoire qui se boucle d'elle-même, à chaque mandat.
                    </motion.p>
                </div>

                <div className="relative grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                    {/* Connecting line desktop (subtile) */}
                    <div className="hidden md:block absolute top-[64px] left-[16%] right-[16%] h-px pointer-events-none">
                        <motion.div
                            initial={{ scaleX: 0 }}
                            animate={inView ? { scaleX: 1 } : {}}
                            transition={{ duration: 1.1, delay: 0.45, ease: 'easeOut' }}
                            className="h-full bg-gradient-to-r from-[#A855F7]/40 via-[#FF914D]/40 to-[#22c55e]/40 origin-left"
                        />
                    </div>

                    {STEPS.map((step, i) => (
                        <motion.div
                            key={step.n}
                            initial={{ opacity: 0, y: 22 }}
                            animate={inView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.55, delay: 0.18 + i * 0.12 }}
                            className="group relative"
                            data-testid={`narrative-step-${i}`}
                        >
                            {/* Step number en arrière-plan */}
                            <span
                                className="absolute -top-3 -left-1 text-[5rem] font-bold leading-none text-white/[0.025] select-none pointer-events-none"
                                style={DISPLAY}
                                aria-hidden="true"
                            >
                                {step.n}
                            </span>

                            <div className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-7 transition-all duration-500 group-hover:border-white/20 group-hover:bg-white/[0.04] group-hover:-translate-y-1">
                                {/* Glow ring on hover */}
                                <div
                                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                                    style={{
                                        boxShadow: `0 0 60px -10px ${step.accent}25, inset 0 0 0 1px ${step.accent}30`,
                                    }}
                                />

                                <div className="relative flex items-center justify-between mb-5">
                                    <div
                                        className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform duration-500 group-hover:scale-110"
                                        style={{ backgroundColor: `${step.accent}18` }}
                                    >
                                        <step.icon className="w-5 h-5" style={{ color: step.accent }} />
                                    </div>
                                    <span
                                        className="text-xs font-medium tracking-widest text-[#A19DAB]"
                                        style={MONO}
                                    >
                                        {step.n}
                                    </span>
                                </div>

                                <h3 className="relative text-lg md:text-xl font-bold text-white mb-2 leading-snug" style={DISPLAY}>
                                    {step.title}
                                </h3>
                                <p className="relative text-sm text-[#A19DAB] leading-relaxed">
                                    {step.desc}
                                </p>

                                {/* Petite flèche d'enchainement sur mobile */}
                                {i < STEPS.length - 1 && (
                                    <div className="md:hidden flex justify-center mt-5">
                                        <ArrowRight className="w-4 h-4 text-[#3B3845] rotate-90" />
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
